import moize from 'moize'
import { isFunction } from 'remeda'

import type { ItemAction } from '@jotta/grpc-js-client/fileService'
import type { FileActionContext } from '@jotta/types/FileActions'
import type { PathItemObject } from '@jotta/types/Files'

import type {
  ActiveFileAction,
  ActiveFileActionType,
} from '../FileActions/fileActionConfig'
import {
  fileActionApi,
  fileActionApiArray,
} from '../FileActions/fileActionConfig'
import { selectionOffersRestore } from '../FileActions/unrestorableBackupPath'

/**
 * Get file actions matching context without checking permissions
 * @param context Filter items by context
 */
export const getFileActionMenuItemsByContext = moize(
  function getFileActionMenuItems(
    context: FileActionContext = 'FOLDER_LIST',
  ): ActiveFileAction<ActiveFileActionType>[] {
    const filteredItems = fileActionApiArray.filter(
      action =>
        !action.hidden &&
        (action.enabledContexts as readonly FileActionContext[]).includes(
          context,
        ),
    ) as ActiveFileAction<ActiveFileActionType>[]
    return filteredItems
  },
  {
    maxSize: 10,
  },
)
/**
 * Get file actions matching context and allowed for path item
 * @param context Filter items by context
 * @param item Path item to check permissions on
 */
export const getFileActionMenuItems = moize.shallow(
  function getFileActionMenuItems(
    context: FileActionContext = 'FOLDER_LIST',
    item: PathItemObject,
    isOwner = true,
  ): ActiveFileAction<ActiveFileActionType>[] {
    const filteredItems = getFileActionMenuItemsByContext(context).filter(
      action =>
        isFunction(action.hasAction)
          ? action.hasAction(item, isOwner)
          : item.actionList.includes(action.hasAction as ItemAction),
    ) as ActiveFileAction<ActiveFileActionType>[]
    return filteredItems
  },
)

export function withSelectionRestore(
  actions: readonly ActiveFileAction<ActiveFileActionType>[],
  items: readonly { path: string; actionList: readonly ItemAction[] }[],
): ActiveFileAction<ActiveFileActionType>[] {
  if (!selectionOffersRestore(items)) {
    return actions.filter(action => action.action !== 'Restore')
  }
  if (actions.some(action => action.action === 'Restore')) {
    return [...actions]
  }
  return [
    ...actions,
    fileActionApi.Restore as ActiveFileAction<ActiveFileActionType>,
  ]
}
